import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import styled, { createGlobalStyle } from 'styled-components';
import { Badge, Button, Select, SECONDARY_BUTTON, SIZE_MEDIUM, Tooltip } from '@mds/mds-reactjs-library';
import {
  BAR_CHART_DEFINITIONS,
  DEFAULT_BAR_CHART_ID,
  buildLiveDashboardCombinedCsv,
  buildLiveDashboardExportData,
  buildLiveDashboardModel,
  buildSelectionSummaryRows,
  downloadCsvContent,
  formatKpiCurrencyHeadingSuffix,
  getEndYearLabel,
  getSelectedCurrency,
  getSelectedYearLabel,
  hasRenderableBarChartData,
  resolveCurrencyDisplayScale,
} from './liveDashboardModel';
import {
  createHistoricBarChartOptions,
  createKpiSummaryChartOptions,
  downloadChartCsv,
  ensureLiveDashboardHighchartsModules,
} from './liveDashboardCharts';
import {
  LIVE_DASHBOARD_BOARD_CONTAINER_ID,
  buildLiveDashboardBoardConfig,
  createLiveDashboardBoard,
  destroyLiveDashboardBoard,
  ensureLiveDashboardBoardModules,
  getBoardHighchartsChart,
} from './liveDashboardBoard';
import { ChartCell, KpiCell, KpiRow, LiveDashboardBoardStyles } from './LiveDashboardBoard.styles';

ensureLiveDashboardHighchartsModules();
ensureLiveDashboardBoardModules();

const METRIC_SELECT_OPTIONS = BAR_CHART_DEFINITIONS.map((definition) => ({
  label: definition.title,
  value: String(definition.id),
}));

const DashboardShell = styled.div`
  width: 100%;
`;

const ChartSection = styled.div`
  width: 100%;
`;

const MetricSelectorRow = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 16px;
`;

const ChartHeaderRow = styled.div`
  align-items: flex-end;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 12px;
  width: 100%;
`;

const ChartHeadingBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const MetricSelectorLabel = styled.span`
  color: #333333;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
`;

const MetricSelectorControl = styled.div`
  flex: 0 1 320px;
  max-width: 350px;
  min-width: 250px;
`;

const DownloadCsvButton = styled(Button)`
  flex-shrink: 0;
  white-space: nowrap;
`;

const ChartHeading = styled.div`
  color: #333333;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.35;
`;

const ChartHeadingCurrency = styled.div`
  color: #757575;
  font-size: 14px;
  line-height: 1.35;
  margin-top: 2px;
`;

const ChartEmptyState = styled.div`
  align-items: center;
  border: 1px solid #dee2eb;
  border-radius: 4px;
  color: #757575;
  display: flex;
  font-size: 14px;
  height: 450px;
  justify-content: center;
  width: 100%;
`;

const SelectionSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
  max-height: calc((14px * 1.4 + 8px) * 3 - 8px);
  overflow-y: auto;
  width: 100%;
`;

const SelectionSummaryRow = styled.div`
  color: #333333;
  font-size: 14px;
  line-height: 1.4;
`;

const SelectionSummaryLabel = styled.span`
  font-weight: 500;
`;

const KpiExportChartHost = styled.div`
  height: 0;
  overflow: hidden;
  position: absolute;
  visibility: hidden;
  width: 0;
`;

const LiveDashboardTooltipStyles = createGlobalStyle`
  .tippy-box .tippy-content .live-dashboard-kpi-tooltip-value,
  .tippy-box[data-theme~='light'] .tippy-content .live-dashboard-kpi-tooltip-value,
  .highcharts-tooltip .live-dashboard-kpi-tooltip-value {
    display: block;
    font-family: 'McKinsey Sans Medium', 'McKinsey Sans Regular', 'Helvetica Neue', Calibri, sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 1.35;
    margin-bottom: 4px;
  }

  .highcharts-tooltip .live-dashboard-kpi-tooltip {
    font-family: 'McKinsey Sans Regular', 'Helvetica Neue', Calibri, sans-serif;
    font-size: 13px;
    line-height: 1.35;
    color: #333333;
  }
`;

const formatKpiHoverContent = (kpi, currencyCode, selectedAttributeNamesByDimension) => {
  if (kpi.value == null || Number.isNaN(kpi.value)) {
    return null;
  }
  const formattedValue = kpi.displayValue;
  if (!formattedValue || formattedValue === '—') {
    return null;
  }

  const yearLabel = kpi.id === 9001 || kpi.id === 9006
    ? getEndYearLabel(selectedAttributeNamesByDimension)
    : getSelectedYearLabel(selectedAttributeNamesByDimension);
  const metricLabel = kpi.hoverName ?? kpi.name;
  const valueWithUnit = kpi.displayUnit
    ? `${formattedValue} ${kpi.displayUnit}`
    : formattedValue;

  return (
    <div className="live-dashboard-kpi-tooltip">
      <div className="live-dashboard-kpi-tooltip-value">{valueWithUnit}</div>
      <div>{metricLabel}</div>
      <div>{`Year: ${yearLabel}`}</div>
      {kpi.displayUnit && <div>{currencyCode}</div>}
    </div>
  );
};

const applyKpiCellPresentation = (dashboardModel) => {
  if (!dashboardModel) {
    return;
  }

  dashboardModel.kpiCards.forEach((kpi) => {
    const cell = document.getElementById(`kpi-${kpi.id}`);
    if (!cell) {
      return;
    }

    cell.style.setProperty('--kpi-value-chars', String(kpi.valueCharCount));

    const componentEl = cell.querySelector('.highcharts-dashboards-component-kpi');
    if (componentEl) {
      componentEl.style.setProperty('background-color', '#2251FF', 'important');
      componentEl.style.setProperty('border-color', '#2251FF', 'important');
      componentEl.style.setProperty('forced-color-adjust', 'none', 'important');
    }

    const titleEl = cell.querySelector('.highcharts-dashboards-component-title');
    if (titleEl) {
      titleEl.style.setProperty('color', '#ffffff', 'important');
    }

    const contentEl = cell.querySelector('.highcharts-dashboards-component-kpi-content')
      ?? cell.querySelector('.highcharts-dashboards-component-content');
    if (contentEl) {
      contentEl.style.height = 'auto';
      contentEl.style.display = 'flex';
      contentEl.style.alignItems = 'center';
      contentEl.style.justifyContent = 'center';
      contentEl.style.padding = '0';
    }

    const valueEl = cell.querySelector('.highcharts-dashboards-component-kpi-value');
    if (valueEl) {
      valueEl.style.setProperty('color', '#ffffff', 'important');
      valueEl.style.fontWeight = '700';
      valueEl.style.overflow = 'visible';
    }
  });
};

const LiveDashboard = forwardRef(({
  chart,
  chartData,
  selectedAttributeNamesByDimension,
  setHeadMessage,
}, ref) => {
  const [selectedBarChartId, setSelectedBarChartId] = useState(DEFAULT_BAR_CHART_ID);
  const boardRef = useRef(null);
  const barChartInstanceRef = useRef(null);
  const kpiChartRef = useRef(null);

  const currencyCode = getSelectedCurrency(selectedAttributeNamesByDimension);
  const yearLabel = getSelectedYearLabel(selectedAttributeNamesByDimension);

  const dashboardModel = useMemo(
    () => buildLiveDashboardModel({
      chartData,
      selectedAttributeNamesByDimension,
      currencyCode,
    }),
    [chartData, selectedAttributeNamesByDimension, currencyCode]
  );

  const exportData = useMemo(
    () => (dashboardModel
      ? buildLiveDashboardExportData({
        kpiCards: dashboardModel.kpiCards,
        barCharts: dashboardModel.barCharts,
        currency: currencyCode,
        selectedAttributeNamesByDimension,
      })
      : null),
    [dashboardModel, currencyCode, selectedAttributeNamesByDimension]
  );

  const selectedBarChart = useMemo(
    () => dashboardModel?.barCharts.find((barChart) => barChart.id === selectedBarChartId) ?? null,
    [dashboardModel, selectedBarChartId]
  );

  const kpiChartOptions = useMemo(() => {
    if (!dashboardModel) {
      return null;
    }
    return createKpiSummaryChartOptions({
      kpiCards: dashboardModel.kpiCards,
      currency: currencyCode,
      yearLabel,
    });
  }, [dashboardModel, currencyCode, yearLabel]);

  const selectedBarChartCurrencyScale = useMemo(
    () => (selectedBarChart?.barData ? resolveCurrencyDisplayScale(selectedBarChart.barData) : null),
    [selectedBarChart],
  );

  const barChartCurrencyHeadingSuffix = useMemo(
    () => formatKpiCurrencyHeadingSuffix(
      currencyCode,
      selectedBarChartCurrencyScale?.unit ?? 'Bn',
    ),
    [currencyCode, selectedBarChartCurrencyScale],
  );

  const barChartOptions = useMemo(() => {
    if (!selectedBarChart || !hasRenderableBarChartData(selectedBarChart.barData)) {
      return null;
    }

    return createHistoricBarChartOptions({
      seriesId: `live-dashboard-bar-${selectedBarChart.id}`,
      metricLabel: selectedBarChart.metricLabel,
      seriesName: selectedBarChart.seriesName,
      categories: selectedBarChart.barCategories,
      data: selectedBarChart.barData,
      currencyDisplayScale: selectedBarChartCurrencyScale,
      displayUnit: selectedBarChartCurrencyScale?.unit ?? 'Bn',
      currencyCode,
      minY: selectedBarChart.minY,
      allowNegative: selectedBarChart.allowNegative ?? false,
    });
  }, [selectedBarChart, selectedBarChartCurrencyScale, currencyCode]);

  const boardConfig = useMemo(() => {
    if (!dashboardModel) {
      return null;
    }

    return buildLiveDashboardBoardConfig({
      dashboardModel,
      barChartOptions,
      includeBarChart: Boolean(barChartOptions),
    });
  }, [dashboardModel, barChartOptions, selectedBarChartId]);

  const selectionSummaryRows = useMemo(
    () => buildSelectionSummaryRows(selectedAttributeNamesByDimension),
    [selectedAttributeNamesByDimension]
  );

  const handleDownloadCombinedCsv = useCallback(() => {
    if (!dashboardModel) {
      return;
    }

    const csv = buildLiveDashboardCombinedCsv({
      kpiCards: dashboardModel.kpiCards,
      selectedBarChart,
    });

    if (!csv) {
      return;
    }

    const barSlug = (selectedBarChart?.title ?? 'dashboard')
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    downloadCsvContent(csv, `live-dashboard-${barSlug}.csv`);
  }, [dashboardModel, selectedBarChart]);

  useImperativeHandle(ref, () => ({
    /** Primary bar chart – used by existing image export in VisualizeByNameContent. */
    get chart() {
      return barChartInstanceRef.current ?? getBoardHighchartsChart(boardRef.current);
    },
    /** KPI summary chart – structured Highcharts series for KPI CSV export. */
    get kpiChart() {
      return kpiChartRef.current?.chart ?? null;
    },
    exportData,
    downloadKpiCsv: () => downloadChartCsv(kpiChartRef.current?.chart),
    downloadBarCsv: () => downloadChartCsv(barChartInstanceRef.current ?? getBoardHighchartsChart(boardRef.current)),
    downloadCombinedCsv: handleDownloadCombinedCsv,
  }), [exportData, handleDownloadCombinedCsv]);

  useEffect(() => {
    setHeadMessage?.('');
  }, [setHeadMessage]);

  useEffect(() => {
    setSelectedBarChartId(DEFAULT_BAR_CHART_ID);
  }, [chartData]);

  useEffect(() => {
    if (!boardConfig) {
      return undefined;
    }

    destroyLiveDashboardBoard(boardRef.current);
    boardRef.current = createLiveDashboardBoard(LIVE_DASHBOARD_BOARD_CONTAINER_ID, boardConfig);

    const frameId = requestAnimationFrame(() => {
      barChartInstanceRef.current = getBoardHighchartsChart(boardRef.current);
      applyKpiCellPresentation(dashboardModel);
    });

    return () => {
      cancelAnimationFrame(frameId);
      destroyLiveDashboardBoard(boardRef.current);
      boardRef.current = null;
      barChartInstanceRef.current = null;
    };
  }, [boardConfig, dashboardModel]);

  return (
    <div className="highchart live-dashboard">
      <LiveDashboardTooltipStyles />
      <LiveDashboardBoardStyles />
      {chartData && dashboardModel ? (
        <DashboardShell>
          {kpiChartOptions ? (
            <KpiExportChartHost aria-hidden="true">
              <HighchartsReact
                highcharts={Highcharts}
                options={kpiChartOptions}
                immutable
                ref={kpiChartRef}
              />
            </KpiExportChartHost>
          ) : null}

          <div className="live-dashboard-board" id={LIVE_DASHBOARD_BOARD_CONTAINER_ID}>
            <KpiRow>
              {dashboardModel.kpiCards.map((kpi) => {
                const hoverContent = formatKpiHoverContent(kpi, currencyCode, selectedAttributeNamesByDimension);
                const cell = (
                  <KpiCell
                    key={kpi.id}
                    id={`kpi-${kpi.id}`}
                    style={{ '--kpi-value-chars': kpi.valueCharCount }}
                  />
                );

                if (!hoverContent) {
                  return cell;
                }

                return (
                  <Tooltip
                    key={kpi.id}
                    dark={false}
                    content={hoverContent}
                    placement="top"
                    interactive
                  >
                    {cell}
                  </Tooltip>
                );
              })}
            </KpiRow>

            <ChartSection>
              <ChartHeaderRow>
                {barChartOptions ? (
                  <ChartHeadingBlock>
                    <ChartHeading>{selectedBarChart.title}</ChartHeading>
                    <ChartHeadingCurrency>{barChartCurrencyHeadingSuffix}</ChartHeadingCurrency>
                  </ChartHeadingBlock>
                ) : (
                  <span />
                )}
                <MetricSelectorRow>
                  <MetricSelectorLabel>Metrics</MetricSelectorLabel>
                  <MetricSelectorControl>
                    <Select
                      inputId="live-dashboard-metric-select"
                      custom
                      size={SIZE_MEDIUM}
                      width="100%"
                      value={String(selectedBarChartId)}
                      options={METRIC_SELECT_OPTIONS}
                      onChange={(value) => {
                        if (value != null && value !== '') {
                          setSelectedBarChartId(Number(value));
                        }
                      }}
                    />
                  </MetricSelectorControl>
                  {/* <DownloadCsvButton
                    appearance={SECONDARY_BUTTON}
                    onClick={handleDownloadCombinedCsv}
                    disabled={!dashboardModel}
                  >
                    Download CSV
                  </DownloadCsvButton> */}
                </MetricSelectorRow>
              </ChartHeaderRow>
              {barChartOptions ? (
                <ChartCell className="live-dashboard-chart-cell" id="live-dashboard-chart" />
              ) : (
                <ChartEmptyState>No data available for the selected metric.</ChartEmptyState>
              )}
              <SelectionSummary>
                {selectionSummaryRows.map((row) => (
                  <SelectionSummaryRow key={row.heading}>
                    <SelectionSummaryLabel>{row.heading}:</SelectionSummaryLabel>{' '}
                    {row.value}
                  </SelectionSummaryRow>
                ))}
              </SelectionSummary>
            </ChartSection>
          </div>
        </DashboardShell>
      ) : (
        <div style={{ position: 'relative' }}>
          <img
            style={{ opacity: 0.3, width: '100%' }}
            src={chart?.preview || (chart && `data:${chart?.thumbnailMimetype};base64,${chart?.thumbnail}`)}
            alt={chart?.title || ''}
          />
          <Badge
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '120px',
              height: '40px',
              fontSize: '16px',
              transform: 'translate(-50%, -50%) rotate(0deg)',
              background: '#B3B3B3',
              color: '#333',
            }}
          >
            PREVIEW
          </Badge>
        </div>
      )}
    </div>
  );
});

LiveDashboard.displayName = 'LiveDashboard';

export default LiveDashboard;
